import {
  buildMeetingInstance, seriesOverrides,
} from '@/modules/meetings/meeting-instance';
import type {
  AgendaItem, Meeting, MeetingSchedule, MeetingType,
} from '@/modules/meetings/types';

/**
 * Whether an occurrence still matches the series it came from (#411).
 *
 * Compared against a rebuild rather than a stored copy of the defaults, using
 * the same pair the materializer wrote the row with, so "unchanged" means the
 * constructor would produce this row again today.
 */
export interface SeriesDivergence {
  /** Off its occurrence instant. The one divergence stored explicitly (#411 D6). */
  moved: boolean;
  host: boolean;
  invitees: boolean;
  agenda: boolean;
  /** Prep email on/off, or its lead time. */
  prep: boolean;
  occurrenceStart: string;
}

/**
 * The row the materializer would write for this occurrence today.
 *
 * `cycle` and `quarterlyCycles` are null because nothing below compares the
 * fields they decide. `actingPersonId` is null to match the materializer —
 * falling back to the reader would make divergence depend on who is looking.
 */
export function seriesBaselineFor(
  meeting: Meeting, schedule: MeetingSchedule, type: MeetingType, tz: string,
): Meeting | null {
  if (!meeting.occurrenceStart) return null;
  return buildMeetingInstance({
    id: meeting.id,
    type,
    scheduledAt: meeting.occurrenceStart,
    workspaceTz: tz,
    cycle: null,
    quarterlyCycles: null,
    actingPersonId: null,
    occurrence: { scheduleId: schedule.id, start: meeting.occurrenceStart },
    overrides: seriesOverrides(schedule),
  });
}

/**
 * A roster as a comparable key. Automatic and empty collapse together, because
 * `meetingFromRow` flattens a NULL column to `[]` and the store cannot tell
 * them apart either.
 */
export const rosterKey = (v: string[] | null | undefined): string =>
  (v && v.length ? [...v].sort().join(',') : '');

const agendaKey = (v: AgendaItem[] | null | undefined): string =>
  JSON.stringify((v ?? []).map((i) => [i.key, i.label, i.length, i.custom]));

/** `null` when this meeting is not a series occurrence, or matches its series. */
export function describeDivergence(
  meeting: Meeting,
  schedule: MeetingSchedule | undefined,
  type: MeetingType | undefined,
  tz: string,
): SeriesDivergence | null {
  if (!schedule || !type || !meeting.occurrenceStart) return null;
  const base = seriesBaselineFor(meeting, schedule, type, tz);
  if (!base) return null;

  const divergence: SeriesDivergence = {
    moved: meeting.scheduledAt !== meeting.occurrenceStart,
    host: (meeting.hostId ?? null) !== (base.hostId ?? null),
    invitees: rosterKey(meeting.invitees) !== rosterKey(base.invitees),
    agenda: agendaKey(meeting.agenda) !== agendaKey(base.agenda),
    prep: (meeting.prepEmailEnabled ?? null) !== (base.prepEmailEnabled ?? null)
      || (meeting.notificationTimingHours ?? null) !== (base.notificationTimingHours ?? null),
    occurrenceStart: meeting.occurrenceStart,
  };

  const { occurrenceStart: _, ...flags } = divergence;
  return Object.values(flags).some(Boolean) ? divergence : null;
}

/** What differs, as the lines the indicator shows on hover. */
export function divergenceLines(divergence: SeriesDivergence, tz: string): string[] {
  const lines: string[] = [];
  if (divergence.moved) {
    lines.push(`Moved from ${new Intl.DateTimeFormat('en-US', {
      timeZone: tz, weekday: 'short', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
    }).format(new Date(divergence.occurrenceStart))}`);
  }
  const changed = [
    divergence.host && 'host',
    divergence.invitees && 'invitees',
    divergence.agenda && 'agenda',
    divergence.prep && 'prep email',
  ].filter(Boolean) as string[];
  if (changed.length) lines.push(`Differs from the series: ${changed.join(', ')}`);
  return lines;
}

/**
 * The edit that puts one occurrence back on its series. Every field is written,
 * including ones that already match, so a gap in the comparison above cannot
 * survive a revert. `null`, not omitted: `updateMeeting` skips `undefined`.
 */
export function revertToSeries(base: Meeting): Partial<Meeting> {
  return {
    scheduledAt: base.scheduledAt,
    date: base.date,
    hostId: base.hostId ?? null,
    invitees: base.invitees,
    agenda: base.agenda ?? null,
    targetLengthMinutes: base.targetLengthMinutes,
    prepEmailEnabled: base.prepEmailEnabled ?? null,
    notificationTimingHours: base.notificationTimingHours ?? null,
  } as Partial<Meeting>;
}
